import { Resource } from './Resources';
import { ActivityQuestion } from './ActivityQuestion';
import { ActivityStatus } from './ActivityStatus';
import { ActivityMeeting, LocationType } from './ActivityMeeting';
import { AudioDocument, ImageDocument, VideoDocument } from './FileDocument';
import { IconSize } from 'assets/icons/icon-sizes';
import QuestionnaireIcon from 'assets/icons/pathway/QuestionnaireIcon';
import TaskIcon from 'assets/icons/pathway/TaskIcon';
import VideoChatIcon from 'assets/icons/pathway/VideoChatIcon';
import { LearnerActivity } from './LearnerCourse';
import LocationIcon from 'assets/icons/controls/LocationIcon';
import { theme } from 'theme';
import ValidateIcon from 'assets/icons/indicators/ValidateIcon';

export type Activity = {
    id: string;
    type: ActivityType;
    title: string;
    description: string;
    content: string;
    order: number;
    resources: Resource[];
    questions: ActivityQuestion[];
    statuses: ActivityStatus[];
    meetings: ActivityMeeting[];
    dateCreated: Date;
    durationInMins: number | null;
    videoDocument: VideoDocument | null;
    imageDocuments: ImageDocument[];
    audioDocument: AudioDocument | null;
    moduleId: string;
    buildMethod: BuildMethod;
    generateContentStatus: GenerateContentStatus | null;

    taskContents: TaskContent[];
};

export type TaskContent =
    | {
          type: 'text';
          id: string;
          order: number;
          text: string;
          audio: { s3Filepath: string };
      }
    | {
          type: 'image';
          id: string;
          order: number;
          imageDescription: string;
          image: {
              id: string;
              filename: string;
              s3Filepath: string;
          };
          audio: { s3Filepath: string };
      };

export enum ActivityType {
    TASK = 'TASK',
    LIVE_TRAINING = 'LIVE_TRAINING',
    QUESTIONNAIRE = 'QUESTIONNAIRE',
    PRESENTATION_SLIDE = 'PRESENTATION_SLIDE',
}

export enum BuildMethod {
    NONE = 'NONE',
    TEXT_AND_IMAGE = 'TEXT_AND_IMAGE',
    VIDEO = 'VIDEO',
    AUDIO = 'AUDIO',
}

export enum GenerateErrorReason {
    NONE = 'NONE',
    MISSING_ENV_VAR_POLLY_REGION = 'Missing environment variable: POLLY_REGION',
    MISSING_ENV_VAR_REGION = 'Missing environment variable: REGION',
    MISSING_ENV_VAR_DOCUMENTS_REGION = 'Missing environment variable: DOCUMENTS_REGION',
    MISSING_ENV_VAR_DOCUMENTS_BUCKET_NAME = 'Missing environment variable: DOCUMENTS_BUCKET_NAME',
    ACTIVITY_NOT_TASK = 'Activity not task',
    TASK_NO_CONTENT = 'Task has no content',
    CONTENT_ALREADY_GENERATED = 'Content already generated',
    POLLY_ERROR = 'Error getting audio from Polly',
    POLLY_RESPONSE_NO_AUDIO = 'Polly response has no audio',
    UPLOAD_AUDIO_ERROR = 'Error uploading audio',
    UPDATE_STATUS_ERROR = 'Error updating status',
}

export enum GenerateType {
    TEXT_TO_AUDIO = 'TEXT_TO_AUDIO',
    AUDIO_TO_TEXT = 'AUDIO_TO_TEXT',
    VIDEO_TO_TEXT = 'VIDEO_TO_TEXT',
    MODULE_QUESTIONNAIRE = 'MODULE_QUESTIONNAIRE',
}

type Status = 'NOT_STARTED' | 'STARTED' | 'GENERATED' | 'ERRORED' | 'APPROVED';

type GenerateContentStatus = {
    id: string;
    generateType: GenerateType;
    dateCreated: Date;
    dateUpdated: Date;
    dateGenerateStart: Date | null;
    dateGenerated: Date | null;
    s3Filepath: string;
    dateErrored: Date | null;
    error: GenerateErrorReason;
    dateApproved: Date | null;
    status: Status;
};

export function getActivityTypeIcon(activity: LearnerActivity, iconSize: IconSize) {
    if (activity.completed) {
        return <ValidateIcon size={iconSize} />;
    }

    switch (activity.type) {
        case ActivityType.LIVE_TRAINING:
            switch (activity.locationType) {
                case LocationType.IN_PERSON:
                    return <LocationIcon size={iconSize} colour={theme.textColour} />;
                case LocationType.ONLINE:
                    return <VideoChatIcon size={iconSize} colour={theme.textColour} />;
                default:
                    return <></>;
            }
        case ActivityType.QUESTIONNAIRE:
            return <QuestionnaireIcon size={iconSize} colour={theme.textColour} />;
        case ActivityType.TASK:
            return <TaskIcon size={iconSize} colour={theme.textColour} />;
        default:
            return <></>;
    }
}
